import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  lookupBudgetCodes,
  type BudgetCode,
  type NamedCodeLike,
  type SearchableCode,
} from '@budgetmonkey/shared'
import { Badge, Button, Input } from '@/components/ui'
import { useBudgetCodes, useCreateExternalBudgetCode } from './hooks'
import { ExternalCodeForm } from './ExternalCodeForm'

/**
 * Budget code picker — workflow 7.7 step 3.
 *
 * Two sections: **My codes** and **External codes**, with one search field
 * filtering both. Owned codes come first because they're the overwhelming
 * majority of selections; external codes are a real but occasional case.
 *
 * Search reuses the ranked lookup from shared/domain, so `580` finds Travel
 * here exactly as it does on the lookup screen — one matching rule, not two
 * that drift apart.
 *
 * Adding an external code is inline rather than a separate trip to settings:
 * the moment you discover the code isn't saved is the moment you're filing the
 * receipt.
 *
 * **Two presentations, one body.** On a pointer-driven screen it's a dropdown
 * anchored under the field, because that's what a select-shaped control should
 * do — a modal that dims the page to pick one value from a list is a heavier
 * gesture than the choice deserves. On a phone the same content is a sheet:
 * an anchored menu on a 375px screen is just a cramped modal with worse
 * ergonomics. The list, search, and add-external flow are identical.
 */

function toSearchable(code: BudgetCode): SearchableCode {
  return {
    id: code.id ?? '',
    code: code.code,
    displayName: code.displayName,
    shortLabel: code.shortLabel,
    aliases: code.aliases,
    external: code.external,
    active: code.active,
  }
}

/** Below this width the dropdown becomes a sheet. Matches Tailwind's `sm`. */
const POINTER_BREAKPOINT = '(min-width: 640px)'

function useAnchoredLayout(): boolean {
  const [anchored, setAnchored] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(POINTER_BREAKPOINT).matches,
  )

  useEffect(() => {
    const query = window.matchMedia(POINTER_BREAKPOINT)
    const update = () => setAnchored(query.matches)
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])

  return anchored
}

interface PickerBodyProps {
  selectedId?: string | undefined
  onSelect: (code: BudgetCode) => void
  onClose: () => void
  fiscalYear?: number | undefined
  /** Focused on open. The search field is the first thing a keyboard user wants. */
  searchRef: React.RefObject<HTMLInputElement | null>
  /** Sheet shows an explicit Close; a dropdown dismisses by clicking away. */
  showCloseButton: boolean
}

function PickerBody({
  selectedId,
  onSelect,
  onClose,
  fiscalYear,
  searchRef,
  showCloseButton,
}: PickerBodyProps) {
  const [query, setQuery] = useState('')
  const [addingExternal, setAddingExternal] = useState(false)

  const { data: allCodes = [] } = useBudgetCodes(fiscalYear)
  const createExternal = useCreateExternalBudgetCode(fiscalYear)

  const byId = useMemo(() => new Map(allCodes.map((c) => [c.id ?? '', c])), [allCodes])
  const searchable = useMemo(() => allCodes.map(toSearchable), [allCodes])

  // Rank once over the whole set, then split. Ranking each section separately
  // would let a weak match in one outrank a strong match in the other.
  const ranked = useMemo(
    () => lookupBudgetCodes(query, searchable, { includeExternal: true, limit: 100 }),
    [query, searchable],
  )
  const owned = ranked.filter((r) => !r.code.external)
  const external = ranked.filter((r) => r.code.external)

  const namedCodes: NamedCodeLike[] = allCodes.map((c) => ({
    id: c.id,
    code: c.code,
    aliases: c.aliases,
    displayName: c.displayName,
  }))

  function choose(id: string) {
    const code = byId.get(id)
    if (code !== undefined) {
      onSelect(code)
      onClose()
    }
  }

  if (addingExternal) {
    return (
      <div className="space-y-4 p-5">
        <h2 className="text-base font-semibold">Add external code</h2>
        <ExternalCodeForm
          existingCodes={namedCodes}
          pending={createExternal.isPending}
          error={createExternal.error?.message}
          onCancel={() => setAddingExternal(false)}
          onSubmit={(values) =>
            createExternal.mutate(values, {
              onSuccess: (created) => {
                // Applied to the current transaction immediately —
                // workflow 7.7 step 6.
                setAddingExternal(false)
                onSelect(created)
                onClose()
              },
            })
          }
        />
      </div>
    )
  }

  return (
    <>
      <div className="sticky top-0 z-10 space-y-3 border-b border-slate-200 bg-white p-4">
        {showCloseButton && (
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-base font-semibold">Budget code</h2>
            <Button variant="ghost" onClick={onClose} aria-label="Close">
              Close
            </Button>
          </div>
        )}
        <Input
          ref={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by alias, code, or name"
          aria-label="Search budget codes"
          autoComplete="off"
        />
      </div>

      <div className="space-y-5 p-4">
        <CodeSection
          title="My codes"
          results={owned}
          selectedId={selectedId}
          onChoose={choose}
          emptyLabel={
            query.trim() === '' ? 'No budget codes yet.' : 'No match among your codes.'
          }
        />

        <div className="space-y-2">
          <CodeSection
            title="External codes"
            results={external}
            selectedId={selectedId}
            onChoose={choose}
            emptyLabel={
              query.trim() === ''
                ? "None saved yet — colleagues' codes you charge on their behalf."
                : 'No match among external codes.'
            }
          />
          <Button type="button" onClick={() => setAddingExternal(true)}>
            Add external code
          </Button>
        </div>
      </div>
    </>
  )
}

interface Position {
  top: number
  left: number
  width: number
  maxHeight: number
}

/**
 * Dropdown anchored under the trigger.
 *
 * Portalled to `<body>` and positioned with fixed coordinates rather than
 * rendered in place. The picker contains its own `<form>` for adding an
 * external code, and it is used inside the transaction form — nested `<form>`
 * elements are invalid HTML, and the inner submit ends up acting on the outer
 * form, which silently abandoned the transaction being entered. Escaping the
 * subtree is what prevents that, so the position has to be computed by hand.
 */
function AnchoredPicker({
  anchorRef,
  onClose,
  ...body
}: Omit<PickerBodyProps, 'searchRef' | 'showCloseButton'> & {
  anchorRef: React.RefObject<HTMLElement | null>
  onClose: () => void
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [position, setPosition] = useState<Position | null>(null)

  const reposition = useCallback(() => {
    const anchor = anchorRef.current
    if (anchor === null) return
    const rect = anchor.getBoundingClientRect()

    const GAP = 6
    const MARGIN = 12
    const spaceBelow = window.innerHeight - rect.bottom - GAP - MARGIN
    const spaceAbove = rect.top - GAP - MARGIN
    // Flip above only when below genuinely can't hold a usable list; a
    // dropdown that jumps sides for a few pixels is more disorienting than a
    // slightly shorter one.
    const flip = spaceBelow < 220 && spaceAbove > spaceBelow
    const maxHeight = Math.min(420, flip ? spaceAbove : spaceBelow)

    setPosition({
      top: flip ? Math.max(MARGIN, rect.top - GAP - maxHeight) : rect.bottom + GAP,
      left: rect.left,
      width: rect.width,
      maxHeight,
    })
  }, [anchorRef])

  // Before paint, so the panel never renders at a stale position first.
  useLayoutEffect(() => {
    reposition()
    searchRef.current?.focus()
  }, [reposition])

  useEffect(() => {
    // `true` captures scrolls in any ancestor, not just the window — the
    // review panel and the transaction form both scroll independently.
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [reposition])

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (panelRef.current?.contains(target) === true) return
      // Clicks on the trigger are its own toggle; closing here too would
      // reopen-and-close in the same gesture.
      if (anchorRef.current?.contains(target) === true) return
      onClose()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        anchorRef.current?.focus()
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [anchorRef, onClose])

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Choose a budget code"
      style={{
        position: 'fixed',
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        // Never narrower than the field, never so wide it covers the form.
        width: Math.max(position?.width ?? 0, 320),
        maxHeight: position?.maxHeight ?? 420,
        visibility: position === null ? 'hidden' : 'visible',
      }}
      className="z-50 overflow-y-auto overscroll-contain rounded-xl border border-slate-200 bg-white shadow-xl"
    >
      <PickerBody
        {...body}
        onClose={onClose}
        searchRef={searchRef}
        showCloseButton={false}
      />
    </div>,
    document.body,
  )
}

/** Full-width sheet for touch screens, where an anchored menu is just cramped. */
function SheetPicker({
  onClose,
  ...body
}: Omit<PickerBodyProps, 'searchRef' | 'showCloseButton'> & { onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // Native <dialog> gives focus trapping, Esc-to-close, and an inert
  // background for free — all fiddly to get right by hand.
  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog !== null && !dialog.open) {
      dialog.showModal()
      searchRef.current?.focus()
    }
  }, [])

  return createPortal(
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose()
      }}
      aria-label="Choose a budget code"
      // `m-auto` is explicit because Tailwind's preflight resets the margin
      // that would otherwise centre a <dialog> — without it the sheet pins
      // itself to the top-left corner.
      className="m-auto max-h-[85vh] w-[min(38rem,92vw)] overflow-y-auto rounded-xl p-0 shadow-xl backdrop:bg-slate-900/40"
    >
      <PickerBody {...body} onClose={onClose} searchRef={searchRef} showCloseButton />
    </dialog>,
    document.body,
  )
}

export function BudgetCodePicker({
  open,
  anchorRef,
  ...rest
}: Omit<PickerBodyProps, 'searchRef' | 'showCloseButton'> & {
  open: boolean
  anchorRef: React.RefObject<HTMLElement | null>
}) {
  const anchored = useAnchoredLayout()
  if (!open) return null
  return anchored ? (
    <AnchoredPicker anchorRef={anchorRef} {...rest} />
  ) : (
    <SheetPicker {...rest} />
  )
}

function CodeSection({
  title,
  results,
  selectedId,
  onChoose,
  emptyLabel,
}: {
  title: string
  results: ReturnType<typeof lookupBudgetCodes>
  selectedId: string | undefined
  onChoose: (id: string) => void
  emptyLabel: string
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h3>
      {results.length === 0 ? (
        <p className="text-sm text-slate-500">{emptyLabel}</p>
      ) : (
        <ul className="space-y-1">
          {results.map(({ code, detail }) => (
            <li key={code.id}>
              <button
                type="button"
                onClick={() => onChoose(code.id)}
                aria-current={code.id === selectedId ? 'true' : undefined}
                className={`flex w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-lg px-3 py-2.5 text-left transition hover:bg-slate-100 ${
                  code.id === selectedId ? 'bg-slate-100 ring-1 ring-slate-900' : ''
                }`}
              >
                <span className="font-medium text-slate-900">{code.shortLabel}</span>
                {code.external && <Badge>External</Badge>}
                <span className="font-mono text-xs text-slate-500">{code.code}</span>
                {detail !== '' && (
                  <span className="ml-auto text-xs text-slate-400">{detail}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** Trigger button plus picker, for use inside forms. */
export function BudgetCodeField({
  value,
  onChange,
  fiscalYear,
  error,
}: {
  value: BudgetCode | null
  onChange: (code: BudgetCode) => void
  fiscalYear?: number | undefined
  error?: string | undefined
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  return (
    <div className="space-y-1.5">
      <span className="block text-sm font-medium text-slate-800">
        Budget code <span className="text-red-600">*</span>
      </span>

      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`flex min-h-11 w-full items-center justify-between gap-3 rounded-lg border bg-white px-3 py-2 text-left shadow-sm transition hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 ${
          error !== undefined ? 'border-red-400' : 'border-slate-300'
        }`}
      >
        {value === null ? (
          <span className="text-slate-400">Choose a budget code</span>
        ) : (
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="font-medium">{value.shortLabel}</span>
            {value.external && <Badge>External</Badge>}
            <span className="font-mono text-xs text-slate-500">{value.code}</span>
          </span>
        )}
        <span aria-hidden="true" className="text-slate-400">
          ▾
        </span>
      </button>

      {error !== undefined && (
        <span role="alert" className="block text-xs font-medium text-red-700">
          {error}
        </span>
      )}

      <BudgetCodePicker
        open={open}
        anchorRef={triggerRef}
        selectedId={value?.id}
        onSelect={onChange}
        onClose={() => setOpen(false)}
        fiscalYear={fiscalYear}
      />
    </div>
  )
}
